import { gsap } from "gsap";
import { Mail, MailCheck, Pencil, Send } from "lucide-react";
import { useLayoutEffect, useMemo, useRef, useState } from "react";
import { z } from "zod";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaForm } from "@/shared/components/forms/ColocaForm";
import { ColocaInputField } from "@/shared/components/forms/ColocaInputField";
import { useZodForm } from "@/shared/components/forms/useZodForm";
import { ColocaReveal } from "@/shared/components/motion/ColocaReveal";
import { MOTION_DURATION, MOTION_EASE } from "@/shared/components/motion/motion.tokens";
import { usePrefersReducedMotion } from "@/shared/hooks/usePrefersReducedMotion";
import type { RegisterTranslations } from "@/shared/lang/interfaces/register";
import useTranslations from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";
import { colocaToast } from "@/shared/utils/toast";

import { useRegister } from "../hooks/useRegister";
import type { ChangeEmailErrorKind } from "../types/register.types";

const CHANGE_EMAIL_ERROR_KEYS: Record<ChangeEmailErrorKind, keyof RegisterTranslations> = {
  unchanged: "verify_change_email_error_same",
  user_in_use: "verify_change_email_error_in_use",
  organization_in_use: "verify_change_email_error_org_in_use",
  already_verified: "verify_error_already_verified",
  invalid_email: "error_email",
  rate_limited: "verify_error_rate_limited",
  generic: "verify_change_email_error_generic",
};

interface VerifyEmailAddressProps {
  email: string;
}

/**
 * Where the code went, and the way out when that address is wrong: the address sits on a label-like chip, and
 * "Change" unfolds it into a field right there. A corrected address gets a fresh code without leaving the
 * screen, so the person keeps their session and signup data instead of registering again.
 */
export function VerifyEmailAddress({ email }: VerifyEmailAddressProps) {
  const { t } = useTranslations("register");
  const [editing, setEditing] = useState(false);
  // Counts successful corrections: the chip plays its "delivered" moment each time it grows.
  const [changes, setChanges] = useState(0);

  if (editing) {
    return (
      <EmailEditor
        currentEmail={email}
        onCancel={() => setEditing(false)}
        onChanged={() => {
          setEditing(false);
          setChanges((count) => count + 1);
        }}
      />
    );
  }

  return (
    <ColocaReveal className="mt-4 flex flex-col items-center gap-2">
      <p data-reveal className="text-sm text-muted-foreground">
        {t(changes > 0 ? "verify_new_code_sent_to_label" : "verify_sent_to_label")}
      </p>
      <div data-reveal className="max-w-full">
        <AddressChip email={email} delivered={changes} onChange={() => setEditing(true)} />
      </div>
    </ColocaReveal>
  );
}

interface AddressChipProps {
  email: string;
  /** Successful corrections so far; each new one replays the "delivered" animation. */
  delivered: number;
  onChange: () => void;
}

function AddressChip({ email, delivered, onChange }: AddressChipProps) {
  const { t } = useTranslations("register");
  const ref = useRef<HTMLDivElement>(null);
  const reduced = usePrefersReducedMotion();

  // The code "leaves" for the new address: a paper plane flies off, the envelope lands checked, and a halo
  // spreads from the chip. GSAP owns the from-states, so without motion the chip is simply in its final state.
  useLayoutEffect(() => {
    const root = ref.current;
    if (!root || reduced || delivered === 0) return;
    const ctx = gsap.context(() => {
      gsap
        .timeline()
        .fromTo(
          "[data-plane]",
          { x: -6, y: 4, opacity: 1, rotate: 0 },
          {
            x: 28,
            y: -18,
            opacity: 0,
            rotate: -12,
            duration: MOTION_DURATION.enter,
            ease: MOTION_EASE.enter,
          },
        )
        .fromTo(
          "[data-envelope]",
          { scale: 0.4, opacity: 0, rotate: -18 },
          { scale: 1, opacity: 1, rotate: 0, duration: MOTION_DURATION.enter, ease: MOTION_EASE.pop },
          MOTION_DURATION.quick,
        )
        .fromTo(
          "[data-address]",
          { opacity: 0, y: 6 },
          { opacity: 1, y: 0, duration: MOTION_DURATION.quick * 2, ease: MOTION_EASE.enter },
          MOTION_DURATION.quick,
        )
        .fromTo(
          "[data-halo]",
          { scale: 0.92, opacity: 0.9 },
          { scale: 1.18, opacity: 0, duration: MOTION_DURATION.slow, ease: MOTION_EASE.enter },
          MOTION_DURATION.quick,
        );
    }, root);
    return () => ctx.revert();
  }, [delivered, reduced]);

  const Envelope = delivered > 0 ? MailCheck : Mail;

  return (
    <div
      ref={ref}
      className={cn(
        "relative inline-flex max-w-full items-center gap-2 rounded-full border bg-card py-1 pr-1 pl-3 shadow-xs",
        delivered > 0 && "border-success/40",
      )}
    >
      {delivered > 0 ? (
        <span
          data-halo
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 rounded-full opacity-0 ring-4 ring-success/40"
        />
      ) : null}
      <span className="relative flex size-5 shrink-0 items-center justify-center">
        <Envelope
          data-envelope
          className={cn("size-4", delivered > 0 ? "text-success" : "text-muted-foreground")}
          aria-hidden="true"
        />
        {delivered > 0 ? (
          <Send data-plane className="absolute size-3.5 text-primary opacity-0" aria-hidden="true" />
        ) : null}
      </span>
      <span data-address className="truncate font-geist-mono text-sm text-foreground" title={email}>
        {email}
      </span>
      <ColocaButton
        type="button"
        variant="ghost"
        size="xs"
        icon={Pencil}
        onClick={onChange}
        aria-label={t("verify_edit_email_aria")}
        className="shrink-0 rounded-full"
      >
        {t("verify_edit_email_link")}
      </ColocaButton>
    </div>
  );
}

interface EmailEditorProps {
  currentEmail: string;
  onCancel: () => void;
  onChanged: () => void;
}

/** Mounted fresh on every "Change", so its schema always compares against the address shown a moment ago. */
function EmailEditor({ currentEmail, onCancel, onChanged }: EmailEditorProps) {
  const { t } = useTranslations("register");
  const { isChangingEmail, doChangeVerificationEmail } = useRegister();
  const [shaking, setShaking] = useState(false);

  const schema = useMemo(
    () =>
      z.object({
        email: z
          .string()
          .trim()
          .min(1, t("error_required"))
          .email(t("error_email"))
          .refine((value) => value.toLowerCase() !== currentEmail.toLowerCase(), {
            message: t("verify_change_email_error_same"),
          }),
      }),
    [t, currentEmail],
  );
  const form = useZodForm<typeof schema>(schema, { email: currentEmail });

  // Land in the field with the old address selected: the usual fix is to retype it, sometimes to touch one letter.
  useLayoutEffect(() => {
    form.setFocus("email", { shouldSelect: true });
  }, [form]);

  const onSubmit = async ({ email }: z.output<typeof schema>) => {
    const refusal = await doChangeVerificationEmail(email);
    if (refusal) {
      form.setError(
        "email",
        { type: "server", message: t(CHANGE_EMAIL_ERROR_KEYS[refusal]) },
        { shouldFocus: true },
      );
      setShaking(true);
      return;
    }
    colocaToast.success(t("verify_change_email_success", { email: email.toLowerCase() }));
    onChanged();
  };

  return (
    <ColocaReveal stagger={0.05} className="mt-4 w-full text-left">
      <div
        className={cn(
          "w-full rounded-xl border bg-card px-5 py-4 shadow-xs",
          shaking && "motion-safe:animate-shake",
        )}
        onAnimationEnd={(event) => {
          if (event.target === event.currentTarget) setShaking(false);
        }}
        onKeyDown={(event) => {
          if (event.key === "Escape" && !isChangingEmail) onCancel();
        }}
      >
        <ColocaForm form={form} onSubmit={onSubmit} onInvalid={() => setShaking(true)} className="space-y-4">
          <div data-reveal>
            <ColocaInputField
              name="email"
              type="email"
              inputMode="email"
              autoComplete="email"
              label={t("verify_change_email_label")}
              placeholder={t("user_email_placeholder")}
              description={t("verify_change_email_hint")}
            />
          </div>
          {/* The step forward is the only primary and always comes last: below on a phone, right on desktop. */}
          <div data-reveal className="flex flex-col gap-2 sm:flex-row sm:justify-end">
            <ColocaButton
              type="button"
              variant="ghost"
              onClick={onCancel}
              disabled={isChangingEmail}
              className="w-full sm:w-auto"
            >
              {t("verify_change_email_cancel")}
            </ColocaButton>
            <ColocaButton
              type="submit"
              variant="primary"
              icon={Send}
              loading={isChangingEmail}
              className="w-full sm:w-auto"
            >
              {isChangingEmail ? t("verify_change_email_submitting") : t("verify_change_email_submit")}
            </ColocaButton>
          </div>
        </ColocaForm>
      </div>
    </ColocaReveal>
  );
}
